import type { PromptPolicy, WorkspacePromptPolicyOverride } from "@/src/types/promptPolicy"

export type WorkspaceOption = { id: string; name: string }

/** Internal workspace list item (API uses workspaceId / workspaceName). */
export type InternalWorkspaceListItem = {
    workspaceId?: string
    id?: string
    workspaceName?: string
    name?: string
}

export function mapInternalWorkspaceOption(item: InternalWorkspaceListItem): WorkspaceOption | null {
    const id = item.workspaceId ?? item.id
    if (!id) return null
    return {
        id,
        name: item.workspaceName ?? item.name ?? id,
    }
}

export function resolveWorkspaceDisplayName(
    workspaceId: string,
    workspaceNameById: Record<string, string>
): string {
    return workspaceNameById[workspaceId] ?? workspaceId
}

export function mergeWorkspaceNameMap(
    prev: Record<string, string>,
    options: WorkspaceOption[]
): Record<string, string> {
    const next = { ...prev }
    for (const option of options) {
        if (option.id) {
            next[option.id] = option.name
        }
    }
    return next
}

export const INTERVIEW_QA_POLICY_NAME = "onlyround-interview-qa-generation-prompt"
export const INTERVIEW_QA_PLACEHOLDER_COUNT = 7

import type { ManagedPromptPoliciesBundle } from "@/src/types/promptPolicy"

export function formatPolicyTypeName(policyType: string): string {
    return policyType
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
}

export function formatTimestamp(isoDate: string): string {
    return new Date(isoDate).toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
    })
}

export function normalizeManagedPromptPolicies(bundle: ManagedPromptPoliciesBundle): Record<string, PromptPolicy> {
    const policies = [
        bundle.onlyroundCriteriaGenerationPrompt,
        bundle.onlyroundInterviewQaGenerationPrompt,
        bundle.scriptGenerationPromptV2,
    ]
    return Object.fromEntries(policies.map((policy) => [policy.name, policy]))
}

export function countFormatPlaceholders(prompt: string): number {
    let count = 0
    let index = 0
    while (index < prompt.length) {
        const found = prompt.indexOf("%s", index)
        if (found < 0) break
        count++
        index = found + 2
    }
    return count
}

export function validateInterviewQaPrompt(prompt: string): string | null {
    const count = countFormatPlaceholders(prompt)
    if (count !== INTERVIEW_QA_PLACEHOLDER_COUNT) {
        return `Interview Q&A prompt must contain exactly ${INTERVIEW_QA_PLACEHOLDER_COUNT} %s placeholders (found ${count}).`
    }
    return null
}

export function validatePromptForPolicy(policyName: string, prompt: string): string | null {
    if (!prompt?.trim()) {
        return "Prompt cannot be empty."
    }
    if (policyName === INTERVIEW_QA_POLICY_NAME) {
        return validateInterviewQaPrompt(prompt)
    }
    return null
}

export function isOverrideStale(
    override: WorkspacePromptPolicyOverride,
    currentGlobalVersion: number | null | undefined
): boolean {
    if (currentGlobalVersion == null || currentGlobalVersion <= 0) return false
    if (override.basedOnGlobalVersionNumber == null) return false
    return override.basedOnGlobalVersionNumber < currentGlobalVersion
}

export function getStaleOverrideTooltip(
    override: WorkspacePromptPolicyOverride,
    currentGlobalVersion: number | null | undefined
): string {
    return `Forked from global v${override.basedOnGlobalVersionNumber}; global is now v${currentGlobalVersion}. Workspace still uses this custom prompt until reset or edited.`
}

export type EffectivePromptResult = {
    source: "global" | "override"
    text: string
    override?: WorkspacePromptPolicyOverride
}

function normalizeWorkspaceId(id: string): string {
    return id.trim().toLowerCase()
}

export function getEffectivePrompt(
    policy: PromptPolicy,
    workspaceId: string,
    overrides: WorkspacePromptPolicyOverride[]
): EffectivePromptResult {
    const normalizedId = normalizeWorkspaceId(workspaceId)
    const override = overrides.find((row) => normalizeWorkspaceId(row.workspaceId) === normalizedId)
    if (override?.config?.prompt) {
        return { source: "override", text: override.config.prompt, override }
    }
    return { source: "global", text: policy.config?.prompt ?? "" }
}
